import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import UrgencyBar from '@/components/feature/UrgencyBar';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import FlooringTypes from './components/FlooringTypes';
import ProcessSteps from './components/ProcessSteps';

const DIFFERENTIATORS = [
  {
    icon: 'ri-survey-line',
    title: 'Subfloor-First Approach',
    desc: 'We never skip subfloor prep. Every install starts with a properly leveled, corrected surface — because the floor is only as good as what\'s underneath.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Moisture Testing',
    desc: 'We test for moisture before any material goes down. This protects your investment from the most common cause of flooring failure.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Detail-Oriented Finish',
    desc: 'Tight seams, clean transitions, perfectly cut edges. We care about the details that most crews rush through.',
  },
  {
    icon: 'ri-time-line',
    title: 'No Rushed Installs',
    desc: 'We don\'t double-book to meet quotas. Your project gets the time it needs — done right the first time.',
  },
];

export default function FlooringInstallationPage() {
  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <UrgencyBar />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/647c6b95d1a1941a06f68b9557a05d3f.jpg"
            alt="Professional flooring installation Tampa Bay"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/92 via-[#0d0d0d]/70 to-[#0d0d0d]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/60 via-transparent to-[#0d0d0d]/20" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#c9a55a]/15 border border-[#c9a55a]/30 rounded-full px-4 py-2 mb-8">
              <i className="ri-map-pin-line text-[#c9a55a] text-sm" />
              <span className="text-[#c9a55a] text-sm font-semibold">Serving Tampa Bay &amp; Sarasota</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
              Professional Flooring{' '}
              <span className="text-[#c9a55a]">Installation</span>{' '}
              Done Right
            </h1>

            <p className="text-white/65 text-xl font-light mb-4 leading-relaxed">
              A great floor starts before installation.
            </p>
            <p className="text-white/50 text-base leading-relaxed mb-10 max-w-xl">
              We prepare, measure, and install with precision — ensuring long-term performance
              for hardwood, LVP, laminate, and engineered wood.
            </p>

            {/* Stats row */}
            <div className="flex flex-wrap gap-6 mb-10">
              {[
                { value: '2000+', label: 'Floors Installed' },
                { value: '8+', label: 'Years Experience' },
                { value: '100%', label: 'Subfloor Prepped' },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <span className="text-[#c9a55a] text-3xl font-black leading-none">{stat.value}</span>
                  <span className="text-white/45 text-xs mt-1 uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 bg-[#c9a55a] hover:bg-[#b8944a] text-[#0d0d0d] font-bold text-lg px-10 py-4 rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line" />
                Get Your Flooring Quote
              </Link>
              <a
                href={`https://wa.me/19044019383?text=${encodeURIComponent('Hi! I\'d like a free estimate for a flooring project.\n\nFloor type: ___\nLocation: ___\nApprox. sq ft: ___')}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/40 text-[#25D366] font-semibold px-8 py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg" />
                Quote via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Banner */}
      <section className="bg-[#111111] border-y border-white/8 py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-8 justify-between">
          <div className="max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3">
              We fix what others ignore — <span className="text-[#c9a55a]">before we install anything.</span>
            </h2>
            <p className="text-white/50 text-base leading-relaxed">
              Moisture issues, uneven subfloors, bad leveling — these are the silent killers of any floor.
              Our team identifies and solves every underlying problem first, so your new floor performs exactly as it should for years to come.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 flex-shrink-0">
            {['Hardwood', 'LVP', 'Laminate', 'Engineered Wood'].map((type) => (
              <span
                key={type}
                className="bg-[#1a1a1a] border border-white/10 text-white/70 text-sm font-medium px-5 py-2.5 rounded-full whitespace-nowrap"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Floor Types */}
      <FlooringTypes />

      {/* Process Steps */}
      <ProcessSteps />

      {/* Why Choose VM */}
      <section className="bg-[#111111] border-t border-white/8 py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">Why VM</p>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                We don't compete on price.<br />
                <span className="text-[#c9a55a]">We deliver results.</span>
              </h2>
              <p className="text-white/50 text-base leading-relaxed mb-10">
                Our clients aren't looking for the cheapest option — they're looking for a crew they can trust
                to do the job right. That's who we are.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-[#c9a55a] hover:bg-[#b8944a] text-[#0d0d0d] font-bold px-10 py-4 rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line" />
                Request a Free Quote
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {DIFFERENTIATORS.map((d) => (
                <div
                  key={d.title}
                  className="bg-[#1a1a1a] border border-white/8 rounded-2xl p-7 hover:border-[#c9a55a]/25 transition-colors"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-[#c9a55a]/10 rounded-xl mb-4">
                    <i className={`${d.icon} text-[#c9a55a] text-xl`} />
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{d.title}</h3>
                  <p className="text-white/45 text-sm leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visual Showcase Row */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-4">
            {[
              {
                src: 'https://readdy.ai/api/search-image?query=beautiful%20finished%20hardwood%20floor%20installation%20in%20modern%20luxury%20open%20plan%20home%2C%20wide%20angle%20view%20showing%20entire%20living%20area%20floor%2C%20warm%20amber%20tones%2C%20perfect%20installation%20with%20no%20visible%20gaps%2C%20high%20end%20residential%20interior%2C%20professional%20result&width=600&height=400&seq=showcase_f1&orientation=landscape',
                label: 'Hardwood — Tampa Bay',
              },
              {
                src: 'https://readdy.ai/api/search-image?query=professional%20worker%20installing%20luxury%20vinyl%20plank%20flooring%20close%20up%2C%20hands%20carefully%20clicking%20LVP%20planks%20together%2C%20grey%20tone%20modern%20floor%20being%20installed%20in%20bright%20residential%20space%2C%20detailed%20craftsmanship%2C%20professional%20installation%20process&width=600&height=400&seq=showcase_f2&orientation=landscape',
                label: 'LVP Installation',
              },
              {
                src: 'https://readdy.ai/api/search-image?query=freshly%20completed%20engineered%20wood%20floor%20in%20elegant%20modern%20dining%20room%2C%20light%20blonde%20wide%20plank%20engineered%20hardwood%2C%20beautiful%20room%20with%20natural%20light%20showing%20perfect%20floor%20finish%2C%20new%20floor%20installation%20completed%20result%2C%20residential%20project&width=600&height=400&seq=showcase_f3&orientation=landscape',
                label: 'Engineered Wood',
              },
            ].map((item) => (
              <div key={item.label} className="relative group rounded-2xl overflow-hidden h-56">
                <img
                  src={item.src}
                  alt={item.label}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white text-sm font-semibold">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#c9a55a] py-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black text-[#0d0d0d] mb-4">
            Ready for a Floor That Lasts?
          </h2>
          <p className="text-[#0d0d0d]/65 text-base mb-8 leading-relaxed">
            Get a free estimate for your flooring project. We serve Tampa Bay &amp; Sarasota.
            Fast response, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#0d0d0d] hover:bg-[#1a1a1a] text-white font-bold text-lg px-12 py-4 rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-line" />
              Get Your Flooring Quote
            </Link>
            <a
              href="tel:+19044019383"
              className="inline-flex items-center gap-2 text-[#0d0d0d] font-bold text-base hover:opacity-75 transition-opacity whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl" />
              (904) 401-9383
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
